import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { slotsApi, devicesApi, settingsWriteApi } from '../lib/api';
import { slotKey, sortByNumber, parseSlotRanges, collapseToRanges, namesBetween, chunk, slotMatches, slotSearchText, parseNumberRanges } from '../lib/slotSelection';
import SlotSourcePicker from '../components/SlotSourcePicker';
import BrowserSlotsPanel from '../components/BrowserSlotsPanel';
import { parseSettingsGrid } from '../lib/settingsGrid';
import ReadinessDialog from '../components/ReadinessDialog';
import PageHeader from '../components/PageHeader';
import { TableScroll } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Plus,
  Search,
  Filter,
  Settings,
  Trash2,
  Play,
  Pause,
  MoreVertical,
  ArrowUpDown,
  Copy,
  Target,
  BarChart3,
  ListChecks,
  Stethoscope,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  HelpCircle,
  Layers,
  Inbox,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  slotState,
  isSlotRunning,
  isSlotSwitchedOn,
  canTogglePause,
  SLOT_STATE,
  PAUSE_EFFECT_NOTE,
  PAUSE_DISABLED_NOTE,
  DEVICE_PAUSED_NOTE,
  NOT_PROVISIONED_NOTE,
} from '../lib/slotState';
import { r14UiEnabled, r16pUiEnabled } from '../lib/r14Switch';
import { storyCount, storyTitle, anyStoryData } from '../lib/storyColumns';
import { unreadCount, unreadText, unreadTitle, anyUnreadData, unreadSortValue } from '../lib/unreadColumn';
import useChatsSummary from '../hooks/useChatsSummary';
import useDoctorMe from '../hooks/useDoctorMe';
import { doctorUiOn } from '../lib/doctorSwitch';
import { rowHolds, rowAttention, needsAttention, anyDoctorData } from '../lib/doctorView';
import SlotBanner from '../components/doctor/SlotBanner';
import DoctorDrawer from '../components/doctor/DoctorDrawer';
import { AttentionDot } from '../components/doctor/DoctorParts';

/* Aurora (2026-08-23): form fields sit one surface step above the glass card they live in. */
const FIELD = 'bg-secondary/60 border-border placeholder:text-muted-foreground/70';

/*
 * Two reasons a control on this page is inert, kept apart because they are fixed differently.
 *
 * NOT_BUILT - the endpoint does not exist on this server (Create, Delete). The UI was ported from
 * V2, whose FastAPI had these routes; this Spring server never did. Needs an endpoint.
 *
 * ADMIN_FIELD - the endpoint is missing AND the field is not the customer's to change. slot_active
 * is V1's ADMIN CAPACITY control: written only behind /userDataManagement/** (ROLE_ADMIN), read by
 * the worker API as a kill switch, and inactive slots are hidden from the customer entirely. V2
 * reinterpreted it as a self-service Active/Inactive toggle and this page inherited that. A
 * customer-facing PAUSE is still wanted - it needs its OWN field, not this one.
 */
const NOT_BUILT_HINT = 'Needs a server endpoint - not built yet';
const ADMIN_FIELD_HINT =
  'Read-only: this reflects the capacity your administrator has provisioned, not a setting you can change';

/*
 * THREE states, not two - that is the whole point. "Not provisioned" is the administrator's doing
 * and the customer cannot change it; "Paused" is the customer's own and they can. Collapsing them
 * into one Active/Inactive chip is exactly the conflation this page used to ship.
 */
const BADGE = {
  [SLOT_STATE.RUNNING]: {
    label: 'Running',
    className: 'border-success/30 bg-success/15 text-success',
    dot: 'bg-success',
  },
  [SLOT_STATE.PAUSED]: {
    label: 'Paused',
    className: 'border-warning/30 bg-warning/15 text-warning',
    dot: 'bg-warning',
  },
  [SLOT_STATE.NOT_PROVISIONED]: {
    label: 'Not provisioned',
    className: 'border-border bg-muted/60 text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
  [SLOT_STATE.DEVICE_PAUSED]: {
    // Dimmed green on purpose: the slot IS switched on (green), but its device is off, so nothing
    // is going to happen (dimmed). Neither the full green of Running nor the grey of Paused.
    label: 'Device off',
    className: 'border-success/25 bg-success/5 text-success/60',
    dot: 'bg-success/40',
  },
};

/*
 * Why a switch is not clickable, in the customer's words. Three distinct reasons, and saying the
 * wrong one is worse than saying nothing: a provisioned slot on a server without the feature is NOT
 * "not provisioned".
 */
function pauseHint(slot) {
  if (slotState(slot) === SLOT_STATE.NOT_PROVISIONED) return NOT_PROVISIONED_NOTE;
  if (!canTogglePause(slot)) return PAUSE_DISABLED_NOTE;
  // Say the useful thing FIRST: flipping this switch will not start anything while the device is
  // off, and someone staring at a green-ish row needs to know that before they try.
  if (slotState(slot) === SLOT_STATE.DEVICE_PAUSED) return DEVICE_PAUSED_NOTE;
  return PAUSE_EFFECT_NOTE;
}

function StatusBadge({ state }) {
  const s = BADGE[state] || BADGE[SLOT_STATE.RUNNING];
  const title =
    state === SLOT_STATE.NOT_PROVISIONED
      ? NOT_PROVISIONED_NOTE
      : state === SLOT_STATE.DEVICE_PAUSED
      ? DEVICE_PAUSED_NOTE
      : state === SLOT_STATE.PAUSED
      ? PAUSE_EFFECT_NOTE
      : undefined;
  return (
    <Badge variant="outline" className={cn('gap-1.5 rounded-full', s.className)} title={title}>
      <span className={cn('h-1.5 w-1.5 rounded-full', s.dot)} aria-hidden />
      {s.label}
    </Badge>
  );
}

/* First load: the three cards the page is made of, as pulsing blocks, so nothing jumps when data lands. */
function SlotsSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-5" aria-busy="true" aria-label="Loading slots" data-testid="slots-skeleton">
      <div className="aurora-card p-4 sm:p-5">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="h-9 flex-1 animate-pulse rounded-xl bg-secondary/60" />
          <div className="h-9 w-full animate-pulse rounded-xl bg-secondary/60 md:w-44" />
          <div className="h-9 w-full animate-pulse rounded-xl bg-secondary/60 md:w-32" />
        </div>
      </div>
      <div className="aurora-card p-4 sm:p-5">
        <div className="h-3 w-56 max-w-full animate-pulse rounded-xl bg-secondary/60" />
        <div className="mt-3 h-9 w-full animate-pulse rounded-xl bg-secondary/60" />
      </div>
      <div className="aurora-card space-y-3 p-4 sm:p-5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 w-full animate-pulse rounded-xl bg-secondary/60" />
        ))}
      </div>
    </div>
  );
}

/*
 * A slot's Target Data and Stats pages, one click from its row (owner, 2026-09-29: "give a direct
 * button/icon for stats and target data pages, like how it's on the V1's dashboard, and not hide
 * it under the 3 dots").
 *
 * V1's bot-settings rows carry three buttons of their own, in this order: Settings, Target Data,
 * Stats. Settings is already direct here - the slot id opens it - so these two follow V1's order.
 * The 3-dots menu keeps both entries, so nothing moved for anyone used to finding them there.
 *
 * Real <Link>s, so ctrl/middle-click opens a new tab like V1's anchors did. stopPropagation keeps
 * the click from reaching anything the row or card listens on (nothing today; a row click handler
 * added later must not also fire, or select the row, when one of these is clicked).
 *
 * Round 14 switch (lib/r14Switch): shown only while it is ON, so OFF stays the round-13 table.
 */
export function slotPagePath(slot, page) {
  return `/slots/${encodeURIComponent(slotKey(slot))}/${page}`;
}

const SLOT_PAGES = [
  { page: 'targets', label: 'Target Data', short: 'Targets', Icon: Target, hint: (k) => `Open the Target Data for ${k}` },
  { page: 'stats', label: 'Stats', short: 'Stats', Icon: BarChart3, hint: (k) => `Open the statistics for ${k}` },
];
/*
 * r16p (design 7.2): the third link, "Inbox" (/slots/<slot>/inbox = /inbox?slot=<slot>), only while the r16p switch is ON and the
 * server's chat routes answer (hooks/useChatsSummary `available`). Without it the row and card are exactly r16n's.
 */
const INBOX_PAGE = { page: 'inbox', label: 'Inbox', short: 'Inbox', Icon: Inbox, hint: (k) => `Open the DM inbox of ${k}` };
const SLOT_PAGES_WITH_INBOX = [...SLOT_PAGES, INBOX_PAGE];

const stopRowClick = (e) => e.stopPropagation();

/* Tinted like the primary chips, so they read as buttons at rest (V1's were solid blue ones). */
const SLOT_LINK_TINT =
  'rounded-lg border border-primary/20 bg-primary/10 text-primary hover:border-primary/40 hover:bg-primary/20 hover:text-primary';

/*
 * The Actions column stays in view while the table scrolls sideways - below about 1400 px the
 * table is wider than its card, and the buttons above would scroll out of sight with the column.
 * Opaque (the glass card's colour) so scrolled cells pass under it; the row's hover tint is
 * layered on so a hovered row still reads as one band.
 */
const STICKY_ACTIONS = 'sticky right-0 z-[1] bg-[color:color-mix(in_srgb,hsl(var(--card))_70%,hsl(var(--background)))]';
const STICKY_ACTIONS_CELL =
  `${STICKY_ACTIONS} group-hover:bg-[image:linear-gradient(hsl(var(--secondary)/0.5),hsl(var(--secondary)/0.5))]`;
/*
 * Drawn on the pinned column's left edge only while columns are scrolled UNDER it (review,
 * 2026-09-29). At 1440 px the pinned column sits on top of "Story likes"; with a flat edge that
 * column looked deleted, not covered. The shadow says "more under here - scroll".
 */
const PINNED_EDGE =
  "before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-3 before:bg-gradient-to-l before:from-black/60 before:to-transparent before:content-[''] "
  + "after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-border after:content-['']";

/*
 * Table row (md+): icon-only buttons, like V1's; the name is in the tooltip and the aria-label.
 * No text label at any width (review, 2026-09-29): the 2xl labels made the pinned column 240 px,
 * so at 1536 / 1600 px - where the table used to fit - it overflowed and hid "Story likes".
 */
function SlotPageRowLinks({ slot, pages = SLOT_PAGES }) {
  const key = slotKey(slot);
  return (
    <TooltipProvider delayDuration={200}>
      {pages.map(({ page, label, Icon, hint }) => (
        <Tooltip key={page}>
          <TooltipTrigger asChild>
            <Button
              asChild
              variant="ghost"
              size="sm"
              className={cn('tap h-8 min-w-8 px-2', SLOT_LINK_TINT)}
            >
              <Link
                to={slotPagePath(slot, page)}
                onClick={stopRowClick}
                aria-label={`${label} for ${key}`}
                data-testid={`slot-${page}-link-${key}`}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="border border-border bg-popover/95 text-foreground backdrop-blur-sm">
            {hint(key)}
          </TooltipContent>
        </Tooltip>
      ))}
    </TooltipProvider>
  );
}

/* Phone card (< md): icon + label, side by side, 40 px tall - a thumb-sized target. r16p: three links get three columns and the short
   labels ("Targets / Stats / Inbox"), so they fit a 320 px phone. */
function SlotPageCardLinks({ slot, pages = SLOT_PAGES }) {
  const key = slotKey(slot);
  const three = pages.length > 2;
  return (
    <div className={cn('grid gap-2', three ? 'grid-cols-3' : 'grid-cols-2')} data-testid={`slot-page-links-card-${key}`}>
      {pages.map(({ page, label, short, Icon }) => (
        <Button
          key={page}
          asChild
          variant="outline"
          size="sm"
          className={cn('h-10 gap-2 text-sm', SLOT_LINK_TINT)}
        >
          <Link
            to={slotPagePath(slot, page)}
            onClick={stopRowClick}
            aria-label={`${label} for ${key}`}
            data-testid={`slot-${page}-link-card-${key}`}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {three ? short : label}
          </Link>
        </Button>
      ))}
    </div>
  );
}

export default function SlotsPageNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  usePageTitle('Slots');
  // Round 14 (lib/r14Switch): Story views / Story likes columns. OFF = the round-13 table.
  const R14 = r14UiEnabled();
  // r16p (lib/r14Switch r16pUiEnabled, design 7.2): the Unread column + card figure (drawn only when the rows carry unread_dms) and the
  // Inbox link (only when the chat routes answer). OFF = the r16n table.
  const R16P = r16pUiEnabled();
  const chats = useChatsSummary({ enabled: R16P });
  const slotPages = R14 && R16P && chats.available ? SLOT_PAGES_WITH_INBOX : SLOT_PAGES;
  // r16sd Settings Doctor (lib/doctorSwitch.js parts slotsList / banner / drawer): the dot and first hold under each slot, the
  // "Needs attention (N)" chip that filters the list (also ?attention=1, the dashboard tile's link) and "Check this slot". Drawn only
  // from the rows' holds / attention and /uiapi/me's doctor: a server without the doctor shows the r16pd page.
  const doctor = useDoctorMe();
  const DOC_LIST = doctorUiOn('slotsList');
  const DOC_BANNER = doctorUiOn('banner');
  const DOC_DRAWER = doctorUiOn('drawer') && doctor.available;
  const [attentionOnly, setAttentionOnly] = useState(() => {
    try { return DOC_LIST && /[?&]attention=1(?:&|$)/.test(window.location.search); } catch (e) { return false; }
  });
  const [doctorSlot, setDoctorSlot] = useState(null); // which slot's doctor drawer is open

  // Is anything scrolled under the pinned Actions column right now? (PINNED_EDGE.) The table's
  // own scroller is the <div> the Table component wraps it in. Watched on scroll and on resize of
  // the scroller or the table (window width, data, fonts); jsdom has no ResizeObserver.
  const [tableScroller, setTableScroller] = useState(null);
  const tableRef = useCallback((el) => setTableScroller(el ? el.parentElement : null), []);
  const [pinnedCovers, setPinnedCovers] = useState(false);
  useEffect(() => {
    if (!R14 || !tableScroller) return undefined;
    const el = tableScroller;
    const update = () => setPinnedCovers(Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 1);
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      el.removeEventListener('scroll', update);
      if (ro) ro.disconnect(); else window.removeEventListener('resize', update);
    };
  }, [R14, tableScroller]);

  // State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('slot_id');
  const [sortOrder, setSortOrder] = useState('asc');

  // ---- selection (U4d/U4e) ---------------------------------------------------------------
  // ONE canonical key everywhere: slot_name. The old code mixed slot_name (select-all) with
  // slot_id (row toggles); on this server they happen to be the same string, but relying on that
  // is a trap, and it made counts unverifiable.
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [rangeExpr, setRangeExpr] = useState('');
  // Shift+click needs the modifier as of pointer-down; onCheckedChange does not carry the event.
  const shiftHeldRef = useRef(false);
  const lastTouchedRef = useRef(null);
  // Bumped whenever a copy run starts or the dialog closes, so an in-flight chunk loop can tell
  // that it no longer owns the dialog and must stop.
  const copyRunRef = useRef(0);

  // ---- bulk copy dialog ------------------------------------------------------------------
  const [copySettingsDialogOpen, setCopySettingsDialogOpen] = useState(false);
  const [readinessSlot, setReadinessSlot] = useState(null); // A4: which slot's readiness dialog is open
  const [copySourceSlot, setCopySourceSlot] = useState('');
  const [copyScope, setCopyScope] = useState('all'); // 'all' | 'sections' | 'ids' | 'grid'
  const [copySections, setCopySections] = useState([]);
  const [copyIdsExpr, setCopyIdsExpr] = useState('');
  const [copyIdentity, setCopyIdentity] = useState(false);
  const [copyGridText, setCopyGridText] = useState('');
  const [copyPreview, setCopyPreview] = useState(null);
  const [copyBusy, setCopyBusy] = useState(null);

  // ---- bulk pause / resume ----------------------------------------------------------------
  // The row switch is the only pause control most customers will ever find, so when the row they
  // flip belongs to a multi-slot selection, the flip has to mean the SELECTION - otherwise pausing
  // 300 slots is 300 clicks. It is never silent: every path lands on the same confirm first, and
  // the result carries an undo built from the slots that actually changed.
  const [bulkPausePlan, setBulkPausePlan] = useState(null); // {enabled, targets, alreadyThere, locked, origin}
  const [bulkPauseBusy, setBulkPauseBusy] = useState(null); // {done, total, enabled}
  const bulkPauseRunRef = useRef(0);

  // Data fetching
  const { data: slots, isLoading } = useQuery({
    queryKey: ['slots'],
    queryFn: () => slotsApi.list().then(res => res.data),
  });

  const { data: devices } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(res => res.data),
  });

  /* Declared up here, above the search filter and the source list, because BOTH of them need the
     friendly device name - a slot row only carries the raw device_id, and matching on the id alone
     means typing what you can see in the Device column finds nothing. */
  const deviceNameFor = (slot) => {
    const deviceId = slot && slot.device_id;
    if (!deviceId) return 'Not assigned';
    // Find device by UUID (d.id) or by deviceN format (d.device_id)
    const device = devices?.find(d => d.id === deviceId || d.device_id === deviceId);
    return device ? (device.label || device.device_name || `device${device.device_number}`) : deviceId;
  };

  // Is the settings write path switched on? (super-admin kill switch UIAPI_SETTINGS_WRITE) and
  // which sections exist, for "copy only these sections".
  //
  // The ERROR matters as much as the answer. A build without the settings-write package answers
  // 404 here, the query fails, and `writesEnabled` is false - the right conclusion, but "the admin
  // switched it off" is the WRONG explanation for it, and it sends the customer to ask an admin
  // who has no such switch to throw.
  const { data: writeInfo, error: writeInfoError } = useQuery({
    queryKey: ['settingsWriteInfo'],
    queryFn: () => settingsWriteApi.info().then(res => res.data),
  });
  const writesEnabled = writeInfo?.enabled === true;
  const writesUnavailable = !!writeInfoError;
  const bulkOffReason = writesUnavailable
    ? `This server has no settings-write endpoint (${writeInfoError.response?.status || 'unreachable'}), so bulk copy cannot run on it.`
    : 'Bulk copy is switched off by the administrator.';

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => slotsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['slots']);
      setCreateDialogOpen(false);
      toast.success('Slot created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create slot');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ slotName, data }) => slotsApi.update(slotName, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['slots']);
      toast.success('Slot updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update slot');
    },
  });

  // Unused while Delete is disabled (NOT_BUILT). Kept, not deleted: the moment
  // DELETE /uiapi/slots/{id} exists, this is the whole wiring.
  // eslint-disable-next-line no-unused-vars
  const deleteMutation = useMutation({
    mutationFn: (slotId) => slotsApi.delete(slotId),
    onSuccess: () => {
      queryClient.invalidateQueries(['slots']);
      setSelectedSlots([]);
      toast.success('Slot(s) deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    },
  });

  // r16sd: the doctor's list parts (dot, chip, filter) only while the server's doctor answers (/me's doctor or the rows' fields).
  // Review MF3: computed BEFORE the filter, so ?attention=1 (the dashboard tile's link, kept in history and bookmarks) filters only
  // while the chip that clears it is drawn - against a server without the doctor (a rollback, r16pd / r16o) every slot is listed.
  const showDoctorList = DOC_LIST && (doctor.available || anyDoctorData(slots));
  const attentionFilter = showDoctorList && attentionOnly;

  // Filtering, sorting, and pagination
  const filteredSlots = useMemo(() => {
    if (!slots) return [];

    return slots.filter(slot => {
      // ONE predicate, shared with the bulk-copy source picker (lib/slotSelection.js), so the two
      // search boxes on this page can never disagree about what a query means. It also matches the
      // RESOLVED device name, which the old inline version did not - typing the device label you
      // can actually see in the table used to match nothing.
      const matchesSearch = slotMatches(slot, searchQuery, deviceNameFor(slot));

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && isSlotRunning(slot)) ||
        (statusFilter === 'paused' && slotState(slot) === SLOT_STATE.PAUSED) ||
        (statusFilter === 'inactive' && slotState(slot) === SLOT_STATE.NOT_PROVISIONED);

      // r16sd: "Needs attention" - a row the server marked with holds or findings (only while the chip is drawn: review MF3)
      const matchesAttention = !attentionFilter || needsAttention(slot);

      return matchesSearch && matchesStatus && matchesAttention;
    });
    // deviceNameFor is re-created every render, so listing it here would defeat the memo entirely.
    // What it actually depends on is `devices`, which IS listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, searchQuery, statusFilter, devices, attentionFilter]);
  const attentionCount = useMemo(() => (showDoctorList ? (slots || []).filter(needsAttention).length : 0), [showDoctorList, slots]);
  const attentionRed = useMemo(
    () => (showDoctorList ? (slots || []).some((s) => { const a = rowAttention(s); return !!a && a.level === 'red'; }) : false),
    [showDoctorList, slots],
  );
  const filtersActive = !!searchQuery || statusFilter !== 'all' || attentionFilter;
  // the row's banner: the dot + first hold; a click opens the drawer while it is available
  const renderDoctorLine = (slot, variant) => {
    if (!DOC_BANNER) return null;
    const holds = rowHolds(slot);
    const attention = rowAttention(slot);
    if (!holds.length && !attention) return null;
    return (
      <SlotBanner holds={holds} attention={attention} variant={variant} slotName={slotKey(slot)} deviceId={slot.device_id}
        onOpen={DOC_DRAWER ? () => setDoctorSlot(slotKey(slot)) : undefined}
        testid={variant === 'strip' ? `slot-banner-card-${slotKey(slot)}` : `slot-banner-${slotKey(slot)}`} />
    );
  };

  const sortedSlots = useMemo(() => {
    if (!filteredSlots) return [];

    return [...filteredSlots].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'slot_id':
          // Sort by slot number (numeric)
          aVal = a.slot_number || 0;
          bVal = b.slot_number || 0;
          break;
        case 'slot_label':
          // Use slot_label column directly
          aVal = (a.slot_label || '').toLowerCase();
          bVal = (b.slot_label || '').toLowerCase();
          break;
        case 'username':
          // Use ig_username column directly
          aVal = (a.ig_username || '').toLowerCase();
          bVal = (b.ig_username || '').toLowerCase();
          break;
        case 'device':
          // Use device_id column directly
          aVal = (a.device_id || '').toLowerCase();
          bVal = (b.device_id || '').toLowerCase();
          break;
        case 'status':
          aVal = isSlotRunning(a) ? 1 : 0;
          bVal = isSlotRunning(b) ? 1 : 0;
          break;
        case 'unread':
          // r16p: real counts by size; "?" (-1) and "not sent" sort below every real count
          aVal = unreadSortValue(a);
          bVal = unreadSortValue(b);
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [filteredSlots, sortField, sortOrder]);

  // r16p: the Unread column is drawn once ANY row of the account carries unread_dms (not just this page's, so it never flickers)
  const showUnread = R16P && anyUnreadData(slots);

  const paginatedSlots = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSlots.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSlots, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedSlots.length / itemsPerPage);

  // Handlers
  const handleCreateSlot = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Create slot with settings in the correct format
    createMutation.mutate({
      slot_label: formData.get('slot_label'),
      device_id: formData.get('device_id'),
      ig_username: formData.get('ig_username'),
      // Also store in settings format for consistency with slot settings page
      settings: {
        slotLabel: formData.get('slot_label'),
        userName: formData.get('ig_username'),
        deviceID: formData.get('device_id'),
      }
    });
  };

  /*
   * The customer's PAUSE. This used to PUT {status} to /uiapi/slots/{slotName} - a route that
   * existed nowhere, and which would have written the ADMIN capacity bit if it had. It now writes
   * one field through one purpose-built route.
   *
   * onError invalidates ['slots'] so a row acting on a stale list (e.g. an admin de-provisioned it
   * since this page loaded, which the server answers 409) re-renders as what it actually is.
   */
  const pauseMutation = useMutation({
    mutationFn: ({ slotName, enabled }) => slotsApi.setEnabled(slotName, enabled),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries(['slots']);
      toast.success(
        vars.enabled ? `${vars.slotName} resumed` : `${vars.slotName} paused`,
        { description: PAUSE_EFFECT_NOTE }
      );
    },
    onError: (error, vars) => {
      queryClient.invalidateQueries(['slots']);
      const code = error.response?.data?.error;
      toast.error(
        code === 'slot_not_provisioned'
          ? `${vars.slotName} is not provisioned`
          : code === 'feature_disabled'
          ? PAUSE_DISABLED_NOTE
          : `Could not update ${vars.slotName}`,
        { description: error.response?.data?.message }
      );
    },
  });

  /*
   * The switch means THE SELECTION when the row it sits on is part of one. A customer who has just
   * picked slot1-slot300 and flips one of them means all 300 - making them do it 300 times is the
   * bug, not the feature. Everything else keeps the old single-row behaviour: an unselected row, or
   * a selection of one, moves only itself.
   *
   * It is never silent. A flip that would touch more than the row lands on the confirm below, which
   * says exactly how many will be written, how many are already there, and how many cannot move -
   * and still offers "just this one".
   */
  const handleTogglePause = (slot) => {
    if (!canTogglePause(slot)) return;
    const name = slotKey(slot);
    const enabled = slotState(slot) === SLOT_STATE.PAUSED; // the state we are flipping TO
    if (selectedSlots.length > 1 && selectedSlots.includes(name)) {
      setBulkPausePlan({ ...planBulkPause(selectedSlots, enabled), origin: name });
      return;
    }
    pauseMutation.mutate({ slotName: name, enabled });
  };

  /* Hover text for the row switch. Inside a multi-slot selection the switch does something bigger
     than its own row, and it has to say so BEFORE the click, not only in the confirm afterwards. */
  const switchHint = (slot) => {
    const base = pauseHint(slot);
    if (canTogglePause(slot) && selectedSlots.length > 1 && selectedSlots.includes(slotKey(slot))) {
      return `Applies to all ${selectedSlots.length} selected slots - you will be asked to confirm. ${base}`;
    }
    return base;
  };

  /**
   * What a pause/resume over these names would ACTUALLY do, in three buckets. Planning before
   * asking is what makes the confirm honest: "pause 300" when 290 are already paused is a lie, and
   * a customer who reads it once stops reading the next one.
   */
  const planBulkPause = (names, enabled) => {
    const targets = [], alreadyThere = [], locked = [];
    names.forEach((n) => {
      const s = slotByName.get(n);
      if (!s) return; // vanished from the list since it was selected
      if (!canTogglePause(s)) locked.push(n);           // not provisioned, or pausing is off
      // The slot's OWN switch, not whether it is running: a device-paused slot is already
      // switched on, so a bulk resume must not claim it as something it will change.
      else if (isSlotSwitchedOn(s) === enabled) alreadyThere.push(n); // nothing to write
      else targets.push(n);
    });
    return { enabled, targets, alreadyThere, locked };
  };

  /* Small enough that one slow slot cannot stall the run, small enough not to flood the server. */
  const PAUSE_PARALLEL = 8;

  /**
   * Write exactly these slots, through the single-slot route that is already live. There is no bulk
   * route on the server, and pretending this is one transaction would be a lie - the writes are
   * independent and some can fail while others land. So the contract is the honest one: report what
   * really changed, and hand back an undo built from exactly that list.
   */
  const executeBulkPause = async (targets, enabled) => {
    if (!targets.length) return;
    const myRun = ++bulkPauseRunRef.current;
    setBulkPauseBusy({ done: 0, total: targets.length, enabled });

    const ok = [];
    const failed = [];
    for (const group of chunk(targets, PAUSE_PARALLEL)) {
      if (bulkPauseRunRef.current !== myRun) return; // superseded by a newer run
      // eslint-disable-next-line no-await-in-loop
      const settled = await Promise.allSettled(group.map((n) => slotsApi.setEnabled(n, enabled)));
      settled.forEach((r, i) => {
        if (r.status === 'fulfilled') ok.push(group[i]);
        else failed.push({ name: group[i], why: r.reason?.response?.data?.error || r.reason?.message || 'failed' });
      });
      setBulkPauseBusy((b) => (b && bulkPauseRunRef.current === myRun ? { ...b, done: b.done + group.length } : b));
    }
    if (bulkPauseRunRef.current !== myRun) return;

    setBulkPauseBusy(null);
    setBulkPausePlan(null);
    queryClient.invalidateQueries(['slots']);

    if (ok.length === 0) {
      toast.error(`Nothing changed - all ${failed.length} write${failed.length === 1 ? '' : 's'} failed`,
        { description: failed[0]?.why });
      return;
    }
    toast.success(
      `${enabled ? 'Resumed' : 'Paused'} ${ok.length} slot${ok.length === 1 ? '' : 's'}`
        + (failed.length ? `; ${failed.length} failed` : ''),
      {
        description: PAUSE_EFFECT_NOTE,
        duration: 12000,
        // The rollback. Built from the slots that ACTUALLY changed, so it can never touch a slot
        // this run failed to write or deliberately skipped.
        action: { label: 'Undo', onClick: () => executeBulkPause(ok, !enabled) },
      });
  };

  // ---- derived selection data --------------------------------------------------------------
  /** name -> row. The selection is a list of NAMES, so every bulk action needs this to read state. */
  const slotByName = useMemo(() => {
    const m = new Map();
    (slots || []).forEach((s) => { const k = slotKey(s); if (k) m.set(k, s); });
    return m;
  }, [slots]);
  const allOwnedNames = useMemo(() => (slots || []).map(slotKey).filter(Boolean), [slots]);
  const allMatchingNames = useMemo(() => sortedSlots.map(slotKey).filter(Boolean), [sortedSlots]);
  const pageNames = useMemo(() => paginatedSlots.map(slotKey).filter(Boolean), [paginatedSlots]);
  const rangeParse = useMemo(() => parseSlotRanges(rangeExpr, allOwnedNames), [rangeExpr, allOwnedNames]);
  const selectionSummary = useMemo(() => collapseToRanges(selectedSlots), [selectedSlots]);
  const pageAllSelected = pageNames.length > 0 && pageNames.every(n => selectedSlots.includes(n));
  const pageSomeSelected = !pageAllSelected && pageNames.some(n => selectedSlots.includes(n));
  const copyTargets = useMemo(
    () => selectedSlots.filter(n => n !== copySourceSlot),
    [selectedSlots, copySourceSlot]);
  /** Selected targets that the current search/filter is hiding - surfaced in the copy dialog. */
  const hiddenTargets = useMemo(
    () => copyTargets.filter(n => !allMatchingNames.includes(n)),
    [copyTargets, allMatchingNames]);

  /*
   * Setting ids the SERVER actually has, so a typed number can be called out before anything is
   * sent - the server aborts a whole bulk copy on one unknown id.
   *
   * It has to come from the server, not from the bundled FIELD_METADATA: that file is a generated
   * copy and has drifted (they disagree on 2 ids in 51-3000), and the ids are SPARSE - 637
   * settings spread across 0..5002 - so 640 is a real setting and no range check would know it.
   * Fetched only when the id box is actually in use, and cached by react-query after that.
   */
  const { data: settingsMetadata } = useQuery({
    queryKey: ['settingsMetadata'],
    queryFn: () => settingsWriteApi.metadata().then(res => res.data),
    enabled: copySettingsDialogOpen && (copyScope === 'ids' || copyScope === 'grid'),
    staleTime: Infinity,
  });
  const knownSettingIds = useMemo(() => {
    const list = settingsMetadata && (settingsMetadata.settings || settingsMetadata);
    if (!Array.isArray(list)) return null; // null = "not loaded yet", so nothing is called a typo
    return new Set(list.map((s) => s && s.id).filter((n) => n !== undefined && n !== null));
  }, [settingsMetadata]);
  const copyIdsParse = useMemo(
    () => parseNumberRanges(copyIdsExpr, knownSettingIds),
    [copyIdsExpr, knownSettingIds]);

  const ownedNameSet = useMemo(() => new Set(allOwnedNames), [allOwnedNames]);
  const copyGridParse = useMemo(
    () => parseSettingsGrid(copyGridText, {
      sourceName: copySourceSlot || undefined,
      knownIds: knownSettingIds || undefined,
      ownedNames: ownedNameSet,
    }),
    [copyGridText, copySourceSlot, knownSettingIds, ownedNameSet]);

  const clearSelection = () => {
    setSelectedSlots([]);
    lastTouchedRef.current = null;
  };

  /** Every slot matching the CURRENT filters, across every page - the count is on the button. */
  const selectAllMatching = () => setSelectedSlots(sortByNumber([...new Set(allMatchingNames)]));

  /** Header checkbox: THIS PAGE only. The banner above the table offers the full matching set. */
  const handleSelectAll = (checked) => {
    setSelectedSlots(prev => checked
      ? sortByNumber([...new Set([...prev, ...pageNames])])
      : prev.filter(n => !pageNames.includes(n)));
  };

  /**
   * Row checkbox. Shift+click extends from the last row touched (file-manager behaviour), so a
   * visible block of 40 rows is two clicks instead of forty.
   */
  const handleSelectSlot = (name, checked) => {
    // Consume the modifier: it is only set by pointerdown, so a later keyboard Space would
    // otherwise inherit a stale `true` and silently select everything back to the old anchor.
    const shift = shiftHeldRef.current;
    shiftHeldRef.current = false;

    const anchor = lastTouchedRef.current;
    // namesBetween is empty when the anchor is not on THIS page (paged/filtered/sorted away).
    // Falling through to the single-row branch is the honest behaviour - the old code unioned an
    // empty span, so the click did nothing at all and the row silently stayed unselected.
    const span = (shift && anchor && anchor !== name) ? namesBetween(pageNames, anchor, name) : [];
    if (span.length > 0) {
      setSelectedSlots(prev => checked
        ? sortByNumber([...new Set([...prev, ...span])])
        : prev.filter(n => !span.includes(n)));
    } else {
      setSelectedSlots(prev => checked
        ? sortByNumber([...new Set([...prev, name])])
        : prev.filter(n => n !== name));
    }
    lastTouchedRef.current = name;
  };

  /**
   * Range expression -> selection. "Select" REPLACES (what you typed is exactly what is selected -
   * predictable), "Add" unions onto what is already there. Both report what was skipped.
   */
  const applyRange = (mode) => {
    if (rangeParse.names.length === 0) {
      if (!rangeExpr.trim()) toast.error('Type a range first, e.g. 5-40, 55');
      else if (rangeParse.outOfRange.length && rangeParse.highestOwned != null) {
        toast.error(`Nothing there - your highest slot is slot${rangeParse.highestOwned}`);
      } else toast.error('That range matched none of your slots');
      return;
    }
    setSelectedSlots(prev => mode === 'add'
      ? sortByNumber([...new Set([...prev, ...rangeParse.names])])
      : sortByNumber(rangeParse.names));
    const notes = [];
    if (rangeParse.trimmed && rangeParse.highestOwned != null) {
      notes.push(`stopped at slot${rangeParse.highestOwned}, your highest`);
    }
    if (rangeParse.unknown.length) notes.push(`${rangeParse.unknown.length} not on this account were skipped`);
    if (rangeParse.invalid.length) notes.push(`ignored: ${rangeParse.invalid.join(', ')}`);
    toast.success(`${rangeParse.names.length} slot(s) ${mode === 'add' ? 'added' : 'selected'}`
      + (notes.length ? ` - ${notes.join('; ')}` : ''));
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // ---- U4d bulk copy -----------------------------------------------------------------------
  // Everything goes through POST /uiapi/slots/bulk-copy: the server reads the source ONCE, strips
  // identity fields (IG username/password/label can never travel - the DEVICE has been an
  // ordinary copyable setting since 2026-08-27), applies each target
  // through the same engine as a normal save, and records an undo entry per target slot.
  // The server caps targets per request, so preview and apply are chunked - which also gives the
  // operator real progress instead of one long spinner.
  const COPY_CHUNK = 40;

  const runBulkCopy = async (dryRun) => {
    if (!copySourceSlot) { toast.error('Pick the slot to copy FROM'); return; }
    if (copyTargets.length === 0) { toast.error('Select at least one target slot'); return; }
    if (copyScope === 'sections' && copySections.length === 0) {
      toast.error('Pick at least one section, or choose "Everything"');
      return;
    }
    if (copyScope === 'ids' && copyIdsParse.ids.length === 0) {
      toast.error(copyIdsExpr.trim()
        ? 'None of those setting numbers exist on this server'
        : 'Type the setting numbers first, e.g. 1-5, 7');
      return;
    }

    // Snapshot the plan: everything below runs against THIS list, so what gets applied is exactly
    // what was previewed even if the selection moves underneath. On apply we deliberately reuse
    // the list the PREVIEW ran against, so the operator can never apply a plan they did not see.
    const targets = dryRun ? [...copyTargets] : [...(copyPreview?.targets || copyTargets)];
    const batches = chunk(targets, COPY_CHUNK);
    const myRun = ++copyRunRef.current;
    setCopyBusy({ phase: dryRun ? 'preview' : 'apply', done: 0, total: targets.length });

    const rows = [];
    let stoppedEarly = null;
    let cancelled = false;
    for (const batch of batches) {
      if (copyRunRef.current !== myRun) { cancelled = true; break; } // dialog closed / re-run
      try {
        // The server reads ids before sections before "everything" (SettingsWriteController:191),
        // so exactly one selector travels.
        const payload = { source: copySourceSlot, targets: batch, dryRun };
        if (copyScope === 'sections') payload.sections = copySections;
        else if (copyScope === 'ids') payload.ids = copyIdsParse.ids;
        if (copyIdentity) payload.allowIdentity = true;
        // eslint-disable-next-line no-await-in-loop
        const res = await settingsWriteApi.bulkCopy(payload);
        (res.data.targets || []).forEach(t => rows.push(t));
        setCopyBusy(b => (b && copyRunRef.current === myRun ? { ...b, done: b.done + batch.length } : b));
      } catch (e) {
        // A chunk failed (flag killed mid-run, network blip, server error). EARLIER CHUNKS ARE
        // ALREADY COMMITTED - each slot is saved independently server-side - so the operator must
        // be told which slots landed instead of just "failed", or they cannot undo them.
        stoppedEarly = e.response?.data?.error || e.message || 'request failed';
        break;
      }
    }
    if (copyRunRef.current !== myRun) return; // a newer run owns the dialog state
    setCopyBusy(null);

    const okRows = rows.filter(r => r.ok);
    const failed = rows.filter(r => !r.ok);
    const changedTotal = okRows.reduce((n, r) => n + (r.changed || 0), 0);
    const willChange = okRows.filter(r => (r.changed || 0) > 0).length;
    const done = rows.length;

    // Always publish what happened, including on the failure path.
    setCopyPreview({
      rows, dryRun, changedTotal, willChange, failed: failed.length,
      targets, stoppedEarly, done, total: targets.length,
    });

    if (!dryRun && done > 0) queryClient.invalidateQueries(['slots']);

    if (stoppedEarly) {
      toast.error(dryRun
        ? `Preview stopped after ${done} of ${targets.length}: ${stoppedEarly}`
        : (willChange > 0
          // Only claim earlier writes when there actually were some - "the first 0 were changed"
          // is worse than saying nothing landed.
          ? `Stopped after ${done} of ${targets.length}: ${stoppedEarly}. ${willChange} slot(s) WERE already changed - undo each from its History.`
          : `Stopped before anything was written (${stoppedEarly}). No slot was changed.`));
    } else if (cancelled) {
      // unreachable for a finished run; kept for symmetry
    } else if (dryRun) {
      toast.success(`Preview: ${changedTotal} value(s) would change across ${willChange} slot(s)`
        + (failed.length ? `; ${failed.length} slot(s) cannot be written` : ''));
    } else {
      toast.success(`Copied ${changedTotal} value(s) across ${willChange} slot(s)`
        + (failed.length ? `; ${failed.length} failed` : '')
        + '. Undo per slot from its History.');
    }
  };

  /*
   * The legacy grid: per-slot values, merged steps 2 and 3.
   *
   * It writes through the ORDINARY single-slot route, one request per slot, rather than through
   * V1's own bulkSave. V1's parser is proven, but it writes straight past the U4 layer and records
   * NO undo - and undo is the thing that makes per-slot passwords safe to set in bulk in the first
   * place. So the grid is parsed here and fanned out over the audited path, which gives every
   * touched slot its own History entry, the same dry-run preview as the other modes, and identity
   * fields (the entire point of a grid) via the allowIdentity the server already understands.
   */
  const runGridWrite = async (dryRun) => {
    const parsed = copyGridParse;
    if (parsed.errors.length || parsed.rows.length === 0) {
      toast.error(parsed.errors[0] || 'Nothing to write - the grid is empty');
      return;
    }
    // Apply reuses the rows the PREVIEW ran against, so what lands is what was seen.
    const plan = dryRun ? parsed.rows : (copyPreview?.gridRows || parsed.rows);
    const myRun = ++copyRunRef.current;
    setCopyBusy({ phase: dryRun ? 'preview' : 'apply', done: 0, total: plan.length });

    const rows = [];
    let stoppedEarly = null;
    for (const group of chunk(plan, 8)) {
      if (copyRunRef.current !== myRun) return;
      // eslint-disable-next-line no-await-in-loop
      const settled = await Promise.allSettled(group.map((g) =>
        settingsWriteApi.put(g.slot, g.values, { dryRun, allowIdentity: true, reason: 'bulk grid' })));
      settled.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          const d = res.value.data || {};
          rows.push({ slot: group[i].slot, ok: true, changed: d.changed || 0,
            unchanged: d.unchanged || 0, changeId: d.changeId || null, diff: d.diff || [] });
        } else {
          rows.push({ slot: group[i].slot, ok: false,
            error: res.reason?.response?.data?.error || res.reason?.message || 'failed' });
        }
      });
      setCopyBusy(b => (b && copyRunRef.current === myRun ? { ...b, done: b.done + group.length } : b));
    }
    if (copyRunRef.current !== myRun) return;
    setCopyBusy(null);

    const okRows = rows.filter(r => r.ok);
    const failed = rows.filter(r => !r.ok);
    const changedTotal = okRows.reduce((n, r) => n + (r.changed || 0), 0);
    const willChange = okRows.filter(r => (r.changed || 0) > 0).length;

    setCopyPreview({
      rows, dryRun, changedTotal, willChange, failed: failed.length,
      targets: plan.map(p => p.slot), gridRows: plan,
      stoppedEarly, done: rows.length, total: plan.length,
    });
    if (!dryRun && rows.length > 0) queryClient.invalidateQueries(['slots']);

    if (dryRun) {
      toast.success(`Preview: ${changedTotal} value(s) would change across ${willChange} slot(s)`
        + (failed.length ? `; ${failed.length} slot(s) cannot be written` : ''));
    } else {
      toast.success(`Wrote ${changedTotal} value(s) across ${willChange} slot(s)`
        + (failed.length ? `; ${failed.length} failed` : '')
        + '. Undo per slot from its History.');
    }
  };

  const openCopyDialog = () => {
    setCopyPreview(null);
    setCopyBusy(null);
    setCopySettingsDialogOpen(true);
  };

  /** From this slot, into everything else that is selected - the flow people actually think in. */
  const copyFromThisSlot = (name) => {
    setCopySourceSlot(name);
    setCopyPreview(null);
    setCopyBusy(null);
    setCopySettingsDialogOpen(true);
  };

  /* The source list, shared by the picker in the selection bar and the one inside the dialog, so
     the two can never drift or disagree about what "the source" is.
     DATA, not JSX: this used to be an array of <SelectItem> elements, which meant the label and
     the @username the customer wants to search on were baked into React children where nothing
     could read them. `display` is the exact same text those items showed. */
  const sourceRows = useMemo(
    () => sortByNumber([...slotByName.keys()]).map((name) => {
      const slot = slotByName.get(name);
      const deviceName = deviceNameFor(slot);
      return {
        name,
        slot,
        deviceName,
        // Built ONCE here, not per keystroke: the picker filters on every character, and
        // re-lowercasing five fields across 10,100 rows each time is the difference between a
        // responsive box and a laggy one on a phone.
        searchText: slotSearchText(slot, deviceName),
        display: `${name}${slot?.slot_label ? ` - ${slot.slot_label}` : ''}`
          + `${slot?.ig_username ? ` (@${slot.ig_username})` : ''}`,
      };
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slotByName, devices]);

  // ---- row pieces shared by the table (md+) and the phone cards (< md) ----------------------
  // Round 14: today's story views / likes (lib/storyColumns). A dash = the server did not send
  // the count (a server before round 14), never a claim that the slot did nothing.
  const renderStory = (slot, kind) => {
    const c = storyCount(slot, kind);
    const label = kind === 'views' ? 'Story views' : 'Story likes';
    return (
      <span className="text-sm tnum" title={storyTitle(c, label)} data-testid={`story-${kind}-${slotKey(slot)}`}>
        {c ? c.today : '-'}
      </span>
    );
  };

  // r16p (lib/unreadColumn): the unread figure. A dash = the server did not send it; "?" = the worker could not read the badge (-1).
  // Above 0 in the warning tone (V1's orange); a link to the slot's inbox while the chat routes answer.
  const renderUnread = (slot) => {
    const c = unreadCount(slot);
    const key = slotKey(slot);
    const hot = !!(c && !c.unknown && c.count > 0);
    const cls = cn('text-sm tnum', hot ? 'font-semibold text-warning' : 'text-muted-foreground');
    if (c && chats.available) {
      return (
        <Link to={`/inbox?slot=${encodeURIComponent(key)}`} onClick={stopRowClick} title={unreadTitle(c)}
          className={cn(cls, 'rounded px-1 hover:underline')} data-testid={`unread-${key}`} aria-label={`${unreadText(c)} unread for ${key}: open the inbox`}>
          {unreadText(c)}
        </Link>
      );
    }
    return <span className={cls} title={unreadTitle(c)} data-testid={`unread-${key}`}>{unreadText(c)}</span>;
  };

  const renderLimits = (slot) => (
    <div className="flex items-center gap-2 text-xs tnum">
      <span className="text-success">F:{slot.limits?.follows_per_day || slot.ig_acc?.follow_limits?.max_per_day || 0}</span>
      <span className="text-danger">L:{slot.limits?.likes_per_day || slot.ig_acc?.like_limits?.max_per_day || 0}</span>
      <span className="text-primary">C:{slot.limits?.comments_per_day || slot.ig_acc?.comment_limits?.max_per_day || 0}</span>
    </div>
  );

  /* The per-slot menu body; each view renders its own trigger so the testids stay distinct. */
  const renderSlotMenu = (slot) => (
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={() => navigate(`/slots/${slotKey(slot)}/settings`)}>
        <Settings className="h-4 w-4 mr-2" />
        Settings
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => setReadinessSlot(slotKey(slot))}>
        <Stethoscope className="h-4 w-4 mr-2" />
        Readiness
      </DropdownMenuItem>
      {DOC_DRAWER && (
        <DropdownMenuItem onClick={() => setDoctorSlot(slotKey(slot))} data-testid={`check-slot-${slotKey(slot)}`}>
          <Stethoscope className="h-4 w-4 mr-2 text-primary" />
          Check this slot
        </DropdownMenuItem>
      )}
      {/* Also direct buttons on the row (SlotPageRowLinks); kept here so nothing moved. */}
      <DropdownMenuItem onClick={() => navigate(slotPagePath(slot, 'targets'))}>
        <Target className="h-4 w-4 mr-2" />
        Target Data
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => navigate(slotPagePath(slot, 'stats'))}>
        <BarChart3 className="h-4 w-4 mr-2" />
        Statistics
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {/* The natural direction: you are looking at the slot you want to spread. Starting here
          names the SOURCE before the dialog opens, which is the question "copy settings to these"
          never answered. */}
      <DropdownMenuItem
        onClick={() => copyFromThisSlot(slotKey(slot))}
        disabled={!writesEnabled || selectedSlots.filter(n => n !== slotKey(slot)).length === 0}
      >
        <Copy className="h-4 w-4 mr-2" />
        Copy FROM here -&gt; {selectedSlots.filter(n => n !== slotKey(slot)).length} selected
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {/* Disabled: NOT_BUILT. DELETE /uiapi/slots/{id} exists on no branch, so this always failed -
          and it failed AFTER the operator had committed to deleting a slot. Same call the bulk
          Delete already makes, disabled the same way. */}
      <DropdownMenuItem disabled className="text-muted-foreground">
        <Trash2 className="h-4 w-4 mr-2" />
        Delete ({NOT_BUILT_HINT})
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  if (isLoading) {
    return (
      <div>
        <PageHeader icon={Layers} title="Slots" description="Loading your slots..." />
        <SlotsSkeleton />
      </div>
    );
  }

  /* < md: one card per slot (1 -> 2 columns). Same handlers and the same shift-click anchor as
     the table rows; the page-level "select this page" checkbox is repeated here because the table
     header that carries it is hidden on phones. */
  const mobileList = (
    <div className="space-y-3 p-3 sm:p-4">
      {paginatedSlots.length > 0 ? (
        <>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              checked={pageAllSelected ? true : (pageSomeSelected ? 'indeterminate' : false)}
              onCheckedChange={(c) => handleSelectAll(c === true || c === 'indeterminate')}
              title="Select the slots on this page"
              data-testid="select-all-checkbox-card"
            />
            Select the {pageNames.length} slot{pageNames.length === 1 ? '' : 's'} on this page
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {paginatedSlots.map((slot) => {
              const key = slotKey(slot);
              const state = slotState(slot);
              return (
                <div
                  key={key}
                  className="flex min-w-0 flex-col gap-3 rounded-xl border border-border/70 bg-secondary/40 p-4"
                  data-testid={`slot-card-${key}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      className="mt-1"
                      checked={selectedSlots.includes(key)}
                      onPointerDown={(e) => { shiftHeldRef.current = e.shiftKey; }}
                      onCheckedChange={(checked) => handleSelectSlot(key, checked)}
                      title="Shift+click to select everything between"
                      data-testid={`select-slot-card-${key}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          onClick={() => navigate(`/slots/${key}/settings`)}
                          className="block max-w-full truncate font-mono text-sm text-primary transition-colors hover:text-primary/80 hover:underline"
                          data-testid={`slot-id-link-card-${key}`}
                        >
                          {key}
                        </button>
                        {showDoctorList && rowAttention(slot) && (
                          <AttentionDot level={rowAttention(slot).level} testid={`attention-dot-card-${key}`} />
                        )}
                      </div>
                      <p className="truncate font-medium">{slot.slot_label || 'N/A'}</p>
                      <p className="truncate font-mono text-xs text-muted-foreground">@{slot.ig_username || 'N/A'}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="tap shrink-0 text-muted-foreground hover:text-foreground"
                          aria-label={`Actions for ${key}`}
                          data-testid={`actions-card-${key}`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      {renderSlotMenu(slot)}
                    </DropdownMenu>
                  </div>
                  {renderDoctorLine(slot, 'strip')}
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-2 xs:grid-cols-2">
                    <div className="min-w-0">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Device</dt>
                      <dd className="truncate text-sm">{deviceNameFor(slot)}</dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Daily limits</dt>
                      <dd className="mt-0.5">{renderLimits(slot)}</dd>
                    </div>
                    {R14 && (
                      <div className="min-w-0">
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Story views / likes today</dt>
                        <dd className="mt-0.5">{renderStory(slot, 'views')}<span className="mx-1 text-muted-foreground">/</span>{renderStory(slot, 'likes')}</dd>
                      </div>
                    )}
                    {showUnread && (
                      <div className="min-w-0" data-testid={`unread-card-${key}`}>
                        <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Unread messages</dt>
                        <dd className="mt-0.5">{renderUnread(slot)}</dd>
                      </div>
                    )}
                  </dl>
                  <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-3">
                    <StatusBadge state={state} />
                    <Switch
                      checked={state !== SLOT_STATE.PAUSED && state !== SLOT_STATE.NOT_PROVISIONED}
                      disabled={!canTogglePause(slot) || pauseMutation.isPending || !!bulkPauseBusy}
                      onCheckedChange={() => handleTogglePause(slot)}
                      title={switchHint(slot)}
                      aria-label={`${key} is ${state}. ${switchHint(slot)}`}
                      data-testid={`toggle-slot-card-${key}`}
                    />
                  </div>
                  {R14 && <SlotPageCardLinks slot={slot} pages={slotPages} />}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {filtersActive
            ? 'No slots match your filters'
            : 'No slots yet. Create your first slot to get started.'}
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Header */}
      <PageHeader
        icon={Layers}
        title="Slots"
        description={
          <>
            {sortedSlots.length} slot{sortedSlots.length !== 1 ? 's' : ''} total
            {selectedSlots.length > 0 && ` • ${selectedSlots.length} selected`}
          </>
        }
        actions={
          <>
            {/* Copy Settings Button */}
            <Button
              variant="outline"
              className="tap"
              onClick={openCopyDialog}
              disabled={!writesEnabled || selectedSlots.length === 0}
              title={!writesEnabled
                ? bulkOffReason
                : selectedSlots.length === 0
                ? 'Select the target slots first (range box below)'
                : `Copies ONE slot's settings INTO the ${selectedSlots.length} you have selected. You choose which slot to copy from next.`}
              data-testid="copy-settings-btn"
            >
              <Copy className="h-4 w-4" />
              {/* The count is the TARGET count, so the arrow says so - the old "(300)" read just as
                  easily as "300 sources". */}
              {copySourceSlot && copyTargets.length > 0
                ? `Copy ${copySourceSlot} -> ${copyTargets.length}`
                : `Copy Settings${selectedSlots.length > 0 ? ` -> ${selectedSlots.length}` : ''}`}
            </Button>
            {/* How bulk copy works - the owner could not work it out from the controls alone. */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="tap text-muted-foreground hover:text-primary"
                  title="How does bulk copy work?"
                  aria-label="How does bulk copy work?"
                  data-testid="bulk-copy-help"
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[calc(100vw-2rem)] space-y-2 text-sm sm:w-80">
                <div className="font-semibold text-foreground">Copy settings from ONE slot to many</div>
                <p className="text-xs text-foreground/90">
                  Two different sets of slots are involved, and mixing them up is the easy mistake:
                  the <b>source</b> is a single slot you copy <b>from</b>, and the <b>targets</b> are
                  the slots you selected, which get written.
                </p>
                <ol className="list-decimal list-inside space-y-1 text-foreground/90">
                  <li><b>Pick the targets</b> - tick slots, shift-click a run, or type a range in the
                    box below the table (e.g. <code>5-40, 55</code>; <code>1-99999</code> means all of yours).</li>
                  <li><b>Pick the source</b> in the "Copy from" box that appears with the selection -
                    or open any slot's <b>...</b> menu and choose "Copy FROM here".</li>
                  <li>Choose <b>what</b> to copy - everything, or only some sections.</li>
                  <li><b>Preview</b> shows exactly which values change on which slots; nothing is written until you confirm.</li>
                </ol>
                <div className="text-xs text-muted-foreground">
                  Pausing works the same way: with several slots selected, flipping one row's switch
                  asks whether to apply it to the whole selection.
                </div>
                <div className="text-xs text-muted-foreground">Every copy is undoable per slot from its settings page (last 20 changes).
                  {!writesEnabled && ' Currently disabled by the admin (feature flag).'}</div>
              </PopoverContent>
            </Popover>

            {/* New Slot Button - disabled: NOT_BUILT. POST /uiapi/slots/ does not exist, so the
                dialog only ever ended in a failed toast after the operator filled the whole form.
                On this server slots are provisioned by an admin (User.noOfDevices), not created
                self-service, so this needs a product decision as well as an endpoint. */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="tap shadow-glow"
                  disabled
                  title={NOT_BUILT_HINT}
                  data-testid="new-slot-btn"
                >
                  <Plus className="h-4 w-4" />
                  New Slot
                </Button>
              </DialogTrigger>
              <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create New Slot</DialogTitle>
                  <DialogDescription>
                    Set up a new Instagram account for automation
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateSlot}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="slot_label">Slot Label</Label>
                      <Input
                        id="slot_label"
                        name="slot_label"
                        placeholder="My Instagram Account"
                        className={FIELD}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ig_username">Instagram Username</Label>
                      <Input
                        id="ig_username"
                        name="ig_username"
                        placeholder="@username"
                        className={FIELD}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="device_id">Device</Label>
                      <Select name="device_id" required>
                        <SelectTrigger className={FIELD}>
                          <SelectValue placeholder="Select a device" />
                        </SelectTrigger>
                        <SelectContent>
                          {devices?.map((device) => (
                            <SelectItem
                              key={device.id}
                              value={device.id}
                            >
                              {device.label || device.device_name} ({device.device_id || `device${device.device_number}`})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="tap">
                      Cancel
                    </Button>
                    <Button type="submit" className="tap">
                      Create Slot
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      {/* A4: per-slot readiness (gap engine) */}
      <ReadinessDialog
        slotName={readinessSlot || ''}
        open={!!readinessSlot}
        onOpenChange={(o) => { if (!o) setReadinessSlot(null); }}
      />

      {/* r16sd: "Check this slot" - the Settings Doctor drawer */}
      {DOC_DRAWER && (
        <DoctorDrawer
          slotName={doctorSlot || ''}
          open={!!doctorSlot}
          onOpenChange={(o) => { if (!o) setDoctorSlot(null); }}
          deviceId={(slots || []).find((s) => slotKey(s) === doctorSlot)?.device_id}
        />
      )}

      {/* Bulk pause / resume confirm. Reached from a row switch inside a multi-slot selection and
          from the Actions menu; both show the same plan, because both do the same thing. */}
      <Dialog
        open={!!bulkPausePlan}
        onOpenChange={(o) => { if (!o && !bulkPauseBusy) setBulkPausePlan(null); }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg rounded-2xl" data-testid="bulk-pause-dialog">
          {bulkPausePlan && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {bulkPausePlan.enabled ? 'Resume' : 'Pause'} {bulkPausePlan.targets.length} slot
                  {bulkPausePlan.targets.length === 1 ? '' : 's'}?
                </DialogTitle>
                <DialogDescription>
                  {bulkPausePlan.origin
                    ? `You flipped ${bulkPausePlan.origin}, and it is one of ${selectedSlots.length} selected slots.`
                    : `This applies to the ${selectedSlots.length} slots you have selected.`}
                  {' '}{PAUSE_EFFECT_NOTE}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 py-2 text-sm">
                <p className="break-words rounded-lg bg-secondary/60 p-2 font-mono text-xs text-foreground/90">
                  {collapseToRanges(selectedSlots, 12)}
                </p>
                <ul className="space-y-1" data-testid="bulk-pause-plan">
                  <li className="flex items-center gap-2">
                    {bulkPausePlan.enabled
                      ? <Play className="h-3.5 w-3.5 shrink-0 text-success" />
                      : <Pause className="h-3.5 w-3.5 shrink-0 text-warning" />}
                    <span>
                      <b className="tnum">{bulkPausePlan.targets.length}</b> will be{' '}
                      {bulkPausePlan.enabled ? 'resumed' : 'paused'}
                    </span>
                  </li>
                  {bulkPausePlan.alreadyThere.length > 0 && (
                    <li className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      <span>
                        <b className="tnum">{bulkPausePlan.alreadyThere.length}</b> already{' '}
                        {bulkPausePlan.enabled ? 'running' : 'paused'} - not written
                      </span>
                    </li>
                  )}
                  {bulkPausePlan.locked.length > 0 && (
                    <li className="flex items-start gap-2 text-warning">
                      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        <b className="tnum">{bulkPausePlan.locked.length}</b> cannot be moved by you
                        (not provisioned by your administrator) - skipped
                      </span>
                    </li>
                  )}
                </ul>
                {bulkPauseBusy ? (
                  <div className="flex items-center gap-2 text-primary tnum" data-testid="bulk-pause-progress">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {bulkPauseBusy.enabled ? 'Resuming' : 'Pausing'} {bulkPauseBusy.done}/{bulkPauseBusy.total}...
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Undo is offered in the confirmation right after this runs, and it only touches
                    the slots that actually changed.
                  </p>
                )}
              </div>

              <DialogFooter className="gap-2">
                {/* The escape hatch. Someone who meant one slot must not have to cancel, deselect
                    everything, and start over. */}
                {bulkPausePlan.origin && (
                  <Button
                    variant="outline"
                    className="tap"
                    disabled={!!bulkPauseBusy}
                    onClick={() => {
                      const { origin, enabled } = bulkPausePlan;
                      setBulkPausePlan(null);
                      pauseMutation.mutate({ slotName: origin, enabled });
                    }}
                    data-testid="bulk-pause-just-one"
                  >
                    Just {bulkPausePlan.origin}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="tap"
                  disabled={!!bulkPauseBusy}
                  onClick={() => setBulkPausePlan(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="tap"
                  disabled={!!bulkPauseBusy || bulkPausePlan.targets.length === 0}
                  onClick={() => executeBulkPause(bulkPausePlan.targets, bulkPausePlan.enabled)}
                  data-testid="bulk-pause-apply"
                >
                  {bulkPausePlan.enabled ? 'Resume' : 'Pause'} {bulkPausePlan.targets.length}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Copy Settings Dialog (U4d) - preview first, then apply; undo per slot afterwards */}
      <Dialog
        open={copySettingsDialogOpen}
        onOpenChange={(o) => {
          setCopySettingsDialogOpen(o);
          if (!o) {
            // Closing means STOP: bump the run id so the chunk loop breaks instead of quietly
            // writing the rest of the slots after the dialog is gone.
            copyRunRef.current++;
            setCopyPreview(null);
            setCopyBusy(null);
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Copy settings to {copyTargets.length} slot{copyTargets.length === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              The device IS copied - one phone runs many accounts, so a shared device is a fleet
              layout, not a mix-up. IG username, password and slot label travel only if you tick the
              box in step 2. Nothing is written until you press Apply, and every slot touched keeps
              an undo entry in its own History.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* The whole operation in one line, before any of the controls. Two slots' worth of
                text answers the question the old dialog left the customer holding: FROM WHERE. */}
            <div
              className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/50 px-3 py-2 text-sm"
              data-testid="copy-flow-strip"
            >
              {copyScope === 'grid' ? (
                <>
                  <span className="font-mono text-primary">the grid below</span>
                  <span className="text-muted-foreground" aria-hidden>--&gt;</span>
                  <span className="font-mono tnum text-foreground/90">
                    {copyGridParse.slotCount} slot{copyGridParse.slotCount === 1 ? '' : 's'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (each slot gets its own values; identity fields included)
                  </span>
                </>
              ) : (
                <>
                  <span className={cn('font-mono', copySourceSlot ? 'text-primary' : 'text-warning')}>
                    {copySourceSlot || 'no source picked yet'}
                  </span>
                  <span className="text-muted-foreground" aria-hidden>--&gt;</span>
                  <span className="font-mono tnum text-foreground/90">
                    {copyTargets.length} slot{copyTargets.length === 1 ? '' : 's'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    (settings travel one way: source is never modified)
                  </span>
                </>
              )}
            </div>

            {/* source */}
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {copyScope === 'grid'
                  ? 'Step 1 - source slot (optional: only what `thisSlot` means)'
                  : 'Step 1 - copy FROM (the source slot)'}
              </Label>
              <SlotSourcePicker
                rows={sourceRows}
                value={copySourceSlot}
                onChange={(v) => { setCopySourceSlot(v); setCopyPreview(null); }}
                className={FIELD}
                placeholder="Pick the slot whose settings you want to spread"
                testId="copy-source-select"
              />
              {!copySourceSlot && copyScope !== 'grid' && (
                <p className="flex items-start gap-1.5 text-xs text-warning" data-testid="no-source-hint">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Nothing can be previewed until you pick the slot the settings come from.
                </p>
              )}
            </div>

            {/* what to copy */}
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Step 2 - what to copy</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button" size="sm"
                  variant={copyScope === 'all' ? 'default' : 'outline'}
                  onClick={() => { setCopyScope('all'); setCopyPreview(null); }}
                  className="tap"
                  data-testid="scope-all-btn"
                >
                  Everything
                </Button>
                <Button
                  type="button" size="sm"
                  variant={copyScope === 'sections' ? 'default' : 'outline'}
                  onClick={() => { setCopyScope('sections'); setCopyPreview(null); }}
                  className="tap"
                  data-testid="scope-sections-btn"
                >
                  Only these sections
                </Button>
                {/* The legacy page's own way of saying it - "Copying only settings 1-5 and 7 to
                    slots 5-10" - so anyone who used V1 types the same expression here. */}
                <Button
                  type="button" size="sm"
                  variant={copyScope === 'ids' ? 'default' : 'outline'}
                  onClick={() => { setCopyScope('ids'); setCopyPreview(null); }}
                  className="tap"
                  data-testid="scope-ids-btn"
                >
                  By setting id
                </Button>
                {/* The legacy grid. It is the only mode that can give each slot a DIFFERENT value,
                    which is what identity fields need - a hundred slots want a hundred logins, not
                    one login copied a hundred times. It carries its own targets, so it swallows
                    step 3 as well. */}
                <Button
                  type="button" size="sm"
                  variant={copyScope === 'grid' ? 'default' : 'outline'}
                  onClick={() => { setCopyScope('grid'); setCopyPreview(null); }}
                  className="tap"
                  data-testid="scope-grid-btn"
                >
                  Per-slot values (grid)
                </Button>
              </div>

              {copyScope === 'grid' && (
                <div className="space-y-1.5" data-testid="grid-picker">
                  <p className="text-xs text-muted-foreground">
                    One row per slot, one column per setting - the same grid the old settings page
                    took. The first row lists the setting ids (its first cell is ignored); each row
                    after it starts with a slot or a slot range. <code>thisSlot</code> means the
                    source slot. This mode brings its own targets, so it replaces step 3, and it can
                    set identity fields because every slot gets its own value.
                  </p>
                  <textarea
                    value={copyGridText}
                    onChange={(e) => { setCopyGridText(e.target.value); setCopyPreview(null); }}
                    rows={7}
                    spellCheck={false}
                    placeholder={'slot,      1,          2,            8\nslot1,     Main Acc,   bella_shop,   hunter2\nslot5-10,  Spare,      ,             \n'}
                    className={cn('w-full rounded-xl border p-3 font-mono text-xs leading-relaxed', FIELD)}
                    data-testid="copy-grid-input"
                  />
                  {copyGridText.trim() && (
                    <div className="space-y-1 text-xs" data-testid="copy-grid-feedback">
                      {copyGridParse.errors.map((e, i) => (
                        <p key={`e${i}`} className="flex items-start gap-1.5 text-danger">
                          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{e}
                        </p>
                      ))}
                      {copyGridParse.warnings.map((w, i) => (
                        <p key={`w${i}`} className="flex items-start gap-1.5 text-warning">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}
                        </p>
                      ))}
                      {copyGridParse.rows.length > 0 && (
                        <p className="tnum text-success">
                          {copyGridParse.valueCount} value{copyGridParse.valueCount === 1 ? '' : 's'}
                          {' '}across {copyGridParse.slotCount} slot
                          {copyGridParse.slotCount === 1 ? '' : 's'}
                          <span className="text-muted-foreground">
                            {' '}• {collapseToRanges(copyGridParse.rows.map(r => r.slot), 8)}
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {copyScope === 'ids' && (
                <div className="space-y-1.5" data-testid="id-picker">
                  <Input
                    value={copyIdsExpr}
                    onChange={(e) => { setCopyIdsExpr(e.target.value); setCopyPreview(null); }}
                    placeholder="e.g.  1-5, 7, 51-3000   -   the setting numbers shown as #1, #2 ... on the slot settings page"
                    className={cn('pl-3 font-mono', FIELD)}
                    data-testid="copy-ids-input"
                  />
                  {copyIdsExpr.trim() && (
                    <p className="text-xs tnum" data-testid="copy-ids-feedback-inner">
                      <span className={copyIdsParse.ids.length ? 'text-success' : 'text-muted-foreground'}>
                        {copyIdsParse.ids.length} setting{copyIdsParse.ids.length === 1 ? '' : 's'}
                      </span>
                      {/* A sweep like 51-3000 legitimately names thousands of ids that do not
                          exist; complaining about 2,364 of them is noise. A handful is a typo
                          worth naming. */}
                      {copyIdsParse.unknown.length > 0 && copyIdsParse.unknown.length <= 20 && (
                        <span className="text-warning"> • no setting numbered {copyIdsParse.unknown.join(', ')}</span>
                      )}
                      {copyIdsParse.invalid.length > 0 && (
                        <span className="text-danger"> • cannot read: {copyIdsParse.invalid.join(', ')}</span>
                      )}
                    </p>
                  )}
                </div>
              )}
              {copyScope === 'sections' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto border border-border rounded-xl bg-secondary/40 p-3" data-testid="section-picker">
                  {(writeInfo?.sections || []).map((sec) => (
                    <label key={sec} className="flex min-w-0 items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={copySections.includes(sec)}
                        onCheckedChange={(c) => {
                          setCopyPreview(null);
                          setCopySections(prev => (c ? [...prev, sec] : prev.filter(x => x !== sec)));
                        }}
                      />
                      <span className="truncate">{sec}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* The identity opt-in. Not a wall - the server takes allowIdentity now and every
                  target keeps an undo entry - but it is off by default because "Everything" is a
                  blunt button and nobody means "give 299 slots the same Instagram login" by it.
                  Undo is PER SLOT, so reversing it is 299 trips through History. */}
              <label
                className={cn('flex cursor-pointer items-start gap-2 rounded-xl border border-border bg-secondary/40 p-3 text-sm',
                  copyScope === 'grid' && 'hidden')}
                data-testid="copy-identity-toggle"
              >
                <Checkbox
                  className="mt-0.5"
                  checked={copyIdentity}
                  onCheckedChange={(c) => { setCopyIdentity(c === true); setCopyPreview(null); }}
                />
                <span className="min-w-0">
                  Also copy the identity fields - IG username, password, slot label
                  <span className="block text-xs text-muted-foreground">
                    Off by default: every target would end up with the source&rsquo;s Instagram login.
                    Undo is per slot, so reversing this on {copyTargets.length} slot
                    {copyTargets.length === 1 ? '' : 's'} means {copyTargets.length} trip
                    {copyTargets.length === 1 ? '' : 's'} through History.
                  </span>
                </span>
              </label>
            </div>

            {/* targets read-back: collapsed ranges ARE the error check.
                The grid carries its own targets, so it replaces this step entirely rather than
                showing a selection that would have nothing to do with what gets written. */}
            <div className={cn('space-y-1', copyScope === 'grid' && 'hidden')}>
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Step 3 - copy TO, the slots you selected ({copyTargets.length})
              </Label>
              <p className="font-mono text-xs text-foreground/90 bg-secondary/60 rounded-lg p-2 break-words" data-testid="copy-targets-summary">
                {copyTargets.length ? collapseToRanges(copyTargets, 12) : 'nothing selected'}
              </p>
              {copySourceSlot && selectedSlots.includes(copySourceSlot) && (
                <p className="text-xs text-muted-foreground">{copySourceSlot} is the source, so it is excluded from the targets.</p>
              )}
              {/* The range box resolves against the WHOLE account while the table shows a filtered
                  view, so a selection can legitimately include slots you cannot currently see.
                  That is allowed - but it must never be a surprise. */}
              {hiddenTargets.length > 0 && (
                <p className="text-xs text-warning flex items-start gap-1.5" data-testid="hidden-targets-warning">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    {hiddenTargets.length} of these are hidden by your current search/filter
                    ({collapseToRanges(hiddenTargets, 6)}) - they will still be written.
                  </span>
                </p>
              )}
            </div>

            {/* progress */}
            {copyBusy && (
              <div className="text-sm text-primary flex items-center gap-2 tnum" data-testid="copy-progress">
                <Loader2 className="h-4 w-4 animate-spin" />
                {copyBusy.phase === 'preview' ? 'Previewing' : 'Applying'} {copyBusy.done}/{copyBusy.total}...
              </div>
            )}

            {/* results */}
            {copyPreview && !copyBusy && (
              <div className="rounded-xl border border-border overflow-hidden" data-testid="copy-results">
                {copyPreview.stoppedEarly && (
                  <div className="px-3 py-2 text-sm bg-danger/10 border-b border-danger/30 text-danger" data-testid="copy-stopped-banner">
                    Stopped after {copyPreview.done} of {copyPreview.total}: {copyPreview.stoppedEarly}
                    {!copyPreview.dryRun && (copyPreview.willChange > 0
                      ? ' - the slots listed below were already changed and can be undone from their History.'
                      : ' - nothing was written.')}
                  </div>
                )}
                <div className="px-3 py-2 text-sm border-b border-border flex flex-wrap gap-x-4 gap-y-1 tnum">
                  <span className={copyPreview.dryRun ? 'text-primary' : 'text-success'}>
                    {copyPreview.dryRun ? 'Preview' : 'Applied'}: {copyPreview.changedTotal} value(s) across {copyPreview.willChange} slot(s)
                  </span>
                  <span className="text-muted-foreground">
                    {copyPreview.rows.length - copyPreview.willChange - copyPreview.failed} already identical
                  </span>
                  {copyPreview.failed > 0 && (
                    <span className="text-danger">{copyPreview.failed} cannot be written</span>
                  )}
                </div>
                {copyPreview.dryRun && (
                  /* The preview runs the server's real validator, so min/max auto-clamps are
                     included. The one thing it cannot predict is a normalisation the save applies
                     while writing the row (it lower-cases the IG username, as the classic settings
                     page has always done) - so an applied count can be a little higher. Anything
                     extra is recorded and undoable, and this note stops that being a surprise. */
                  <p className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border/70">
                    The save also normalises a few fields as it writes (it lower-cases the IG
                    username, exactly as the classic settings page does), so the applied count can be
                    slightly higher; those changes are recorded and undoable. On COMBO slots it also
                    re-syncs the IG username to the group's parent slot - that one field is re-derived
                    on every save, so it cannot be undone on its own.
                  </p>
                )}
                <div className="max-h-52 overflow-y-auto divide-y divide-border/70">
                  {copyPreview.rows.filter(r => !r.ok || (r.changed || 0) > 0).slice(0, 200).map((r) => (
                    <div key={r.slot} className="px-3 py-1.5 text-xs flex items-center gap-2">
                      {r.ok
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                        : <XCircle className="h-3.5 w-3.5 text-danger shrink-0" />}
                      <span className="font-mono text-foreground/90 w-24 shrink-0 truncate">{r.slot}</span>
                      <span className={cn('min-w-0 break-words', r.ok ? 'text-muted-foreground' : 'text-danger')}>
                        {r.ok ? `${r.changed} value(s) ${copyPreview.dryRun ? 'would change' : 'changed'}` : r.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCopySettingsDialogOpen(false)}
              disabled={!!copyBusy}
              title={copyBusy ? 'Finishing the current batch...' : ''}
              className="tap"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={() => (copyScope === 'grid' ? runGridWrite(true) : runBulkCopy(true))}
              disabled={!!copyBusy || (copyScope === 'grid'
                ? (copyGridParse.errors.length > 0 || copyGridParse.rows.length === 0)
                : (!copySourceSlot || copyTargets.length === 0))}
              className="tap border-primary/40 text-primary"
              data-testid="copy-preview-btn"
            >
              Preview
            </Button>
            {/* Apply stays disabled until a preview has been seen - you cannot mass-write blind. */}
            <Button
              onClick={() => {
                if (window.confirm(`Apply to ${copyPreview?.willChange ?? 0} slot(s)? Each keeps an undo entry in its History.`)) {
                  if (copyScope === 'grid') runGridWrite(false); else runBulkCopy(false);
                }
              }}
              disabled={!!copyBusy || !copyPreview || !copyPreview.dryRun || copyPreview.changedTotal === 0}
              className="tap"
              data-testid="copy-apply-btn"
            >
              Apply to {copyPreview?.willChange ?? 0} slot(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters and Bulk Actions */}
      <Card className="aurora-card">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            {/* Search */}
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, slot ID, or username..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={cn('pl-10', FIELD)}
                data-testid="search-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:flex sm:flex-wrap sm:items-center">
              {/* r16sd: the slots the Settings Doctor marked (holds or findings); a toggle that filters the list */}
              {showDoctorList && (
                <button
                  type="button"
                  onClick={() => { setAttentionOnly((v) => !v); setCurrentPage(1); }}
                  aria-pressed={attentionOnly}
                  disabled={attentionCount === 0 && !attentionOnly}
                  className={cn(
                    'tap inline-flex h-10 items-center justify-center gap-2 rounded-md px-3.5 text-sm font-medium ring-1 transition-colors xs:col-span-2 sm:col-span-1',
                    attentionOnly
                      ? 'bg-primary/15 text-foreground ring-primary/40'
                      : attentionCount > 0
                        ? (attentionRed ? 'bg-danger/10 text-foreground ring-danger/35 hover:bg-danger/15' : 'bg-warning/10 text-foreground ring-warning/35 hover:bg-warning/15')
                        : 'bg-secondary/60 text-muted-foreground ring-border',
                  )}
                  title={attentionOnly ? 'Show every slot again' : 'Show only the slots that need attention'}
                  data-testid="attention-chip"
                >
                  {attentionCount > 0 && <AttentionDot level={attentionRed ? 'red' : 'amber'} className="h-2 w-2" />}
                  <span>{attentionCount > 0 ? 'Needs attention' : 'Nothing needs attention'}</span>
                  {attentionCount > 0 && <span className="tnum rounded-full bg-background/50 px-1.5 text-xs" data-testid="attention-count">{attentionCount}</span>}
                </button>
              )}
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}>
                <SelectTrigger className={cn('w-full sm:w-44', FIELD)} aria-label="Filter by status">
                  <Filter className="h-4 w-4 mr-2 shrink-0 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Slots</SelectItem>
                  <SelectItem value="active">Running only</SelectItem>
                  <SelectItem value="paused">Paused only</SelectItem>
                  <SelectItem value="inactive">Not provisioned</SelectItem>
                </SelectContent>
              </Select>

              {/* Items Per Page */}
              <Select value={itemsPerPage.toString()} onValueChange={(val) => {
                setItemsPerPage(parseInt(val));
                setCurrentPage(1);
              }}>
                <SelectTrigger className={cn('w-full sm:w-32', FIELD)} aria-label="Slots per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                  <SelectItem value="200">200 / page</SelectItem>
                </SelectContent>
              </Select>

              {/* Bulk Actions */}
              {selectedSlots.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="tap xs:col-span-2 border-primary/40 text-primary" data-testid="bulk-actions-btn">
                      Actions ({selectedSlots.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Bulk actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={openCopyDialog}
                      disabled={!writesEnabled}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      {copySourceSlot
                        ? `Copy FROM ${copySourceSlot} into these ${copyTargets.length}`
                        : 'Copy FROM one slot into these (pick the source next)'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* Resume / Pause are the CUSTOMER's own on-off (user_enabled), which has a live
                        endpoint - not slot_active, which is admin capacity and must never appear as
                        a customer control. Both land on the same confirm as the row switch. */}
                    <DropdownMenuItem
                      onClick={() => setBulkPausePlan({ ...planBulkPause(selectedSlots, true), origin: null })}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Resume selected ({selectedSlots.length})
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setBulkPausePlan({ ...planBulkPause(selectedSlots, false), origin: null })}
                    >
                      <Pause className="h-4 w-4 mr-2" />
                      Pause selected ({selectedSlots.length})
                    </DropdownMenuItem>
                    {/* Still disabled on purpose: DELETE /uiapi/slots/{id} is not implemented on this
                        server, and leaving it clickable would 404 only AFTER the operator had
                        committed to deleting 100 slots. */}
                    <DropdownMenuItem disabled className="text-muted-foreground">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete (needs a server endpoint - not built)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection toolbar (U4d) - ranges, not 100 checkboxes */}
      <Card className="aurora-card">
        <CardContent className="space-y-3 p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="min-w-0 flex-1">
              <Label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <ListChecks className="h-3.5 w-3.5 shrink-0" />
                Select by range - the fast way to pick 100+ slots
              </Label>
              <div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
                <Input
                  value={rangeExpr}
                  onChange={(e) => setRangeExpr(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyRange('replace'); } }}
                  placeholder="e.g.  5-40, 55, 60-70   -   or 1-99999 for every slot you own"
                  className={cn('min-w-0 flex-1 pl-3 font-mono', FIELD)}
                  data-testid="range-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => applyRange('replace')}
                    disabled={!rangeParse.names.length}
                    className="tap flex-1 sm:flex-none"
                    data-testid="range-select-btn"
                  >
                    Select
                  </Button>
                  <Button
                    onClick={() => applyRange('add')}
                    disabled={!rangeParse.names.length}
                    variant="outline"
                    className="tap flex-1 sm:flex-none"
                    data-testid="range-add-btn"
                  >
                    Add
                  </Button>
                </div>
              </div>
              {rangeExpr.trim() && (
                <p className="text-xs mt-1.5 tnum" data-testid="range-feedback">
                  <span className={rangeParse.names.length ? 'text-success' : 'text-muted-foreground'}>
                    {rangeParse.names.length} match{rangeParse.names.length === 1 ? '' : 'es'}
                  </span>
                  {rangeParse.trimmed && rangeParse.highestOwned != null && (
                    <span className="text-muted-foreground"> • stopped at slot{rangeParse.highestOwned}, your highest</span>
                  )}
                  {rangeParse.outOfRange.length > 0 && (
                    <span className="text-warning">
                      {rangeParse.highestOwned != null
                        ? ` • nothing at ${rangeParse.outOfRange.join(', ')} - your highest slot is slot${rangeParse.highestOwned}`
                        : ` • no slots to match against yet`}
                    </span>
                  )}
                  {rangeParse.unknown.length > 0 && (
                    <span className="text-warning"> • {rangeParse.unknown.length} not on this account ({collapseToRanges(rangeParse.unknown, 3)})</span>
                  )}
                  {rangeParse.invalid.length > 0 && (
                    <span className="text-danger"> • cannot read: {rangeParse.invalid.join(', ')}</span>
                  )}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="tap" onClick={selectAllMatching} data-testid="select-all-matching-btn">
                Select all {sortedSlots.length} matching
              </Button>
              <Button variant="ghost" className="tap" onClick={clearSelection} disabled={!selectedSlots.length} data-testid="clear-selection-btn">
                Clear
              </Button>
            </div>
          </div>

          {selectedSlots.length > 0 && (
            <div className="space-y-2.5 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid="selection-summary">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-primary font-medium text-sm tnum">{selectedSlots.length} selected</span>
                <span className="min-w-0 flex-1 font-mono text-xs text-foreground/90 truncate">{selectionSummary}</span>
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">these are the targets</span>
                {/* An Undo from the toast runs with no dialog open, so the progress has to live
                    somewhere on the page or the run looks like nothing happened. */}
                {bulkPauseBusy && !bulkPausePlan && (
                  <span className="flex items-center gap-1.5 text-xs text-primary tnum" data-testid="bulk-pause-progress-inline">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {bulkPauseBusy.enabled ? 'Resuming' : 'Pausing'} {bulkPauseBusy.done}/{bulkPauseBusy.total}
                  </span>
                )}
              </div>
              {/* "Copy settings to these" answered "to WHERE" and never "FROM where" - the source
                  lived inside the dialog, one click out of sight, so the button asked the customer
                  to commit to a copy whose source they could not see. The whole sentence now reads
                  on the page: FROM one slot -> INTO the ones highlighted above. */}
              <div className="flex flex-wrap items-center gap-2">
                <Label
                  htmlFor="copy-source-inline"
                  className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                >
                  Copy from
                </Label>
                <SlotSourcePicker
                  id="copy-source-inline"
                  rows={sourceRows}
                  value={copySourceSlot}
                  onChange={(v) => { setCopySourceSlot(v); setCopyPreview(null); }}
                  className={cn('w-full min-w-0 sm:w-64', FIELD)}
                  testId="copy-source-inline"
                />
                <span className="text-sm text-muted-foreground" aria-hidden>-&gt;</span>
                <span className="text-sm text-foreground/90 tnum">
                  these {copyTargets.length} slot{copyTargets.length === 1 ? '' : 's'}
                </span>
                <Button
                  size="sm"
                  onClick={openCopyDialog}
                  disabled={!writesEnabled}
                  title={writesEnabled ? undefined : bulkOffReason}
                  className="tap ml-auto"
                  data-testid="copy-to-selected-btn"
                >
                  <Copy className="h-4 w-4" />
                  {copySourceSlot ? `Preview ${copySourceSlot} -> these` : 'Choose a source, then copy'}
                </Button>
              </div>
            </div>
          )}

          {!writesEnabled && (writeInfo || writesUnavailable) && (
            <p className="text-xs text-warning flex items-start gap-1.5" data-testid="bulk-disabled-banner">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {bulkOffReason} Pausing and resuming still work.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Data Table - or the honest empty card when nothing matches at all */}
      {sortedSlots.length === 0 ? (
        <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="slots-empty">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
            <Layers className="h-6 w-6" />
          </span>
          {filtersActive ? (
            <>
              <h3 className="mt-4">No slots match your filters</h3>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
                Try a different search or switch the status filter back to all slots.
              </p>
            </>
          ) : (
            <>
              <h3 className="mt-4">No slots yet</h3>
              {/* Honest empty state: on this server slots are provisioned by an administrator, so
                  "create your first slot" was advice the customer cannot act on - and the button
                  behind it posted to an endpoint that does not exist (NOT_BUILT). */}
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
                Slots are provisioned by your administrator. Contact them to have slots added to
                your account.
              </p>
            </>
          )}
        </div>
      ) : (
      <Card className="aurora-card overflow-hidden">
        <CardContent className="p-0">
          {/* Scope is never ambiguous: the header checkbox takes THIS PAGE, this offers the rest. */}
          {pageAllSelected && sortedSlots.length > pageNames.length && selectedSlots.length < sortedSlots.length && (
            <div className="px-4 py-2 text-sm bg-primary/10 border-b border-primary/25 text-foreground/90" data-testid="select-all-banner">
              All {pageNames.length} slots on this page are selected.
              <button onClick={selectAllMatching} className="ml-2 underline text-primary hover:text-primary/80">
                Select all {sortedSlots.length} matching
              </button>
            </div>
          )}
          <TableScroll mobile={mobileList} className="rounded-none">
            <Table ref={tableRef} data-pinned-covers={R14 && pinnedCovers ? 'true' : undefined}>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={pageAllSelected ? true : (pageSomeSelected ? 'indeterminate' : false)}
                      onCheckedChange={(c) => handleSelectAll(c === true || c === 'indeterminate')}
                      title="Select the slots on this page"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="w-32 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('slot_id')}>
                    <div className="flex items-center gap-2">
                      Slot ID
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('slot_label')}>
                    <div className="flex items-center gap-2">
                      Slot Label
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('username')}>
                    <div className="flex items-center gap-2">
                      Instagram
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('device')}>
                    <div className="flex items-center gap-2">
                      Device
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort('status')}>
                    <div className="flex items-center gap-2">
                      Status
                      <ArrowUpDown className="h-4 w-4" />
                    </div>
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Daily Limits</TableHead>
                  {R14 && (
                    <TableHead className="whitespace-nowrap text-right" data-testid="col-story-views"
                      title={anyStoryData(paginatedSlots) ? 'Stories viewed today (hover a number for the last 4 days)' : 'This server does not report story counts yet'}>
                      Story views
                    </TableHead>
                  )}
                  {R14 && (
                    <TableHead className="whitespace-nowrap text-right" data-testid="col-story-likes"
                      title={anyStoryData(paginatedSlots) ? 'Stories liked today (hover a number for the last 4 days)' : 'This server does not report story counts yet'}>
                      Story likes
                    </TableHead>
                  )}
                  {showUnread && (
                    <TableHead className="cursor-pointer select-none whitespace-nowrap text-right" data-testid="col-unread" onClick={() => toggleSort('unread')}
                      title="Unread Instagram messages at the last session (click a number to open that slot's inbox)">
                      <div className="flex items-center justify-end gap-2">
                        Unread
                        <ArrowUpDown className="h-4 w-4" />
                      </div>
                    </TableHead>
                  )}
                  <TableHead className={cn('text-right', R14 && STICKY_ACTIONS, R14 && pinnedCovers && PINNED_EDGE)}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSlots.length > 0 ? (
                  paginatedSlots.map((slot) => {
                    // r16sd: the doctor line, a row of its own under the slot's row (the label column is too narrow for a sentence)
                    const doctorLine = renderDoctorLine(slot, 'line');
                    return (
                    <React.Fragment key={slotKey(slot)}>
                    <TableRow
                      className={cn('border-border hover:bg-secondary/50', R14 && 'group', doctorLine && 'border-b-0')}
                      data-testid={`slot-row-${slotKey(slot)}`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedSlots.includes(slotKey(slot))}
                          onPointerDown={(e) => { shiftHeldRef.current = e.shiftKey; }}
                          onCheckedChange={(checked) => handleSelectSlot(slotKey(slot), checked)}
                          title="Shift+click to select everything between"
                          data-testid={`select-slot-${slotKey(slot)}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/slots/${slotKey(slot)}/settings`)}
                            className="font-mono text-sm text-primary hover:text-primary/80 hover:underline transition-colors"
                            data-testid={`slot-id-link-${slotKey(slot)}`}
                          >
                            {slotKey(slot)}
                          </button>
                          {showDoctorList && rowAttention(slot) && (
                            <AttentionDot level={rowAttention(slot).level} testid={`attention-dot-${slotKey(slot)}`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <p className="font-medium truncate max-w-[14rem]">{slot.slot_label || 'N/A'}</p>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <span className="block font-mono text-sm truncate max-w-[12rem]">@{slot.ig_username || 'N/A'}</span>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <span className="block text-sm truncate max-w-[12rem]">{deviceNameFor(slot)}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {/* ALWAYS rendered. This is the customer's own on/off for the slot and it
                              belongs in the row whatever state the slot is in - hiding it made the
                              column look like it had lost a control. It is bound to the pause, never
                              to slot_active. The only case it cannot be moved is a slot the
                              administrator has not provisioned, and that says so on hover. */}
                          <Switch
                            checked={slotState(slot) !== SLOT_STATE.PAUSED && slotState(slot) !== SLOT_STATE.NOT_PROVISIONED}
                            disabled={!canTogglePause(slot) || pauseMutation.isPending || !!bulkPauseBusy}
                            onCheckedChange={() => handleTogglePause(slot)}
                            title={switchHint(slot)}
                            aria-label={`${slotKey(slot)} is ${slotState(slot)}. ${switchHint(slot)}`}
                            data-testid={`toggle-slot-${slotKey(slot)}`}
                          />
                          <StatusBadge state={slotState(slot)} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderLimits(slot)}
                      </TableCell>
                      {R14 && <TableCell className="text-right">{renderStory(slot, 'views')}</TableCell>}
                      {R14 && <TableCell className="text-right">{renderStory(slot, 'likes')}</TableCell>}
                      {showUnread && <TableCell className="text-right">{renderUnread(slot)}</TableCell>}
                      <TableCell className={cn('text-right', R14 && STICKY_ACTIONS_CELL, R14 && pinnedCovers && PINNED_EDGE)}>
                        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                          {R14 && <SlotPageRowLinks slot={slot} pages={slotPages} />}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="tap text-muted-foreground hover:text-foreground"
                                aria-label={`Actions for ${slotKey(slot)}`}
                                data-testid={`actions-${slotKey(slot)}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            {renderSlotMenu(slot)}
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                    {doctorLine && (
                      <TableRow className="border-border hover:bg-transparent" data-testid={`slot-doctor-row-${slotKey(slot)}`}>
                        <TableCell className="pb-3 pt-0" />
                        <TableCell colSpan={7 + (R14 ? 2 : 0) + (showUnread ? 1 : 0)} className="pb-3 pt-0">{doctorLine}</TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    {/* r16p review: the Unread column is the 11th when it is drawn */}
                    <TableCell colSpan={(R14 ? 10 : 8) + (showUnread ? 1 : 0)} className="text-center py-8 text-muted-foreground" data-testid="slots-empty-cell">
                      {filtersActive
                        ? 'No slots match your filters'
                        : 'No slots yet. Create your first slot to get started.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableScroll>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground tnum">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedSlots.length)} of {sortedSlots.length} slots
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="tap"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        className="tap tnum"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="tap"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Per-slot Multilogin browser connection + controls - same page of slots as the table above. */}
      <BrowserSlotsPanel slots={paginatedSlots} />
    </div>
  );
}
